/* eslint-disable max-lines */
import { EditOutlined, LinkOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { getMiitelWidget } from '@salescore/client-base'
import {
  getOrganizationIdFromPath,
  type ProgressType,
  SanitizedHtml,
  useMe,
  UserAvatar,
} from '@salescore/client-common'
import { selectGetUser, useAppSelector } from '@salescore/client-redux'
import {
  CORE_CONSTANT,
  getRecordNodeParents,
  type SheetCell,
  type ViewQueryRecordNode,
  type ViewQueryRecordNodeWithParent,
} from '@salescore/core'
import { organizationHasFeature, useHover } from '@salescore/frontend-common'
import { Button, Popover, Select, Tag } from 'antd'
import { Array, Record } from 'effect'
import { t } from 'i18next'
import type { CSSProperties, ReactElement } from 'react'
import { useLongPress } from 'react-use'
import { z } from 'zod'

import type {
  PivotColumn,
  PivotRowColumn,
  PivotSystemColumn,
  RSheetRecordNode,
  RSheetsSelectOption,
} from '../../../domain/column.ts'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes.ts'
import { useViewQueryResult } from '../../../hooks/useViewQueryResult.ts'
import type { KpiTableContext } from '../../../recoil/context.ts'
import { useCursorMutation } from '../../../recoil/cursor.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { openContextMenu, setEditMode, toggleEditMode } from '../../../rsheet/components/body/cell/cellAction.ts'
import { KpiCell } from '../../../rsheet/components/body/cell/kpi/KpiCell.tsx'
import { RowKpiCell } from '../../../rsheet/components/body/cell/kpi/RowKpiCell.tsx'
import { MINIMUM_WIDTH } from '../../../rsheet/components/body/const.tsx'
import type { SetMainCursorByRecordIdArguments } from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'

export const KpiRowCell = ({
  sheetCell,
  rootRecordNode,
  column,
  rowIndex,
  columnIndex,
  isNewRecord,
  isFieldChanged,
  overrideValue,
  rowHeight,
  context,
  columns,
  setMainCursorByRecordId,
  view,
  setDragging,
  expandOnDrag,
  progress,
  ability,
}: {
  sheetCell: SheetCell
  rootRecordNode: RSheetRecordNode
  column: PivotRowColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  isNewRecord: boolean
  isFieldChanged: boolean
  overrideValue?: unknown
  rowHeight: number
  context: KpiTableContext
  columns: Array<
    // eslint-disable-next-line sonarjs/use-type-alias
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  view: ViewFieldsFragment
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  progress: ProgressType
  ability: ViewAbility & SalescoreObjectAbility
  // eslint-disable-next-line complexity,sonarjs/cognitive-complexity
}): ReactElement => {
  const { innerRowIndexStart, height, recordNode, value } = sheetCell
  const innerRowIndex = innerRowIndexStart
  const { width } = column
  const snapshots = useViewQueryResult(view)?.snapshots ?? {}
  const snapshot = snapshots[column.node.name]?.[recordNode?.id ?? ''] ?? {}

  // 行にコメント入力中かの判定

  const sheetStyle: CSSProperties = {
    border: `1px ${RSheetsStyle.color.border} solid`,
  }
  const kpiStyle: CSSProperties = {
    // KPIセルのとき、横の罫線は表示しない
    borderBottom: `1px ${RSheetsStyle.color.border} solid`,
  }
  const defaultStyle: CSSProperties = {
    width,
    height: height * rowHeight,
    position: 'relative',
    overflow: column.type === 'kpi' ? undefined : 'hidden',
    transition: RSheetsStyle.cellTransition,
    ...(column.type === 'kpi' ? kpiStyle : sheetStyle),
  }

  if (recordNode === undefined) {
    return (
      <KpiRowEmptyCell
        rowIndex={rowIndex}
        columnIndex={columnIndex}
        innerRowIndex={innerRowIndexStart}
        defaultStyle={defaultStyle}
        setDragging={setDragging}
        expandOnDrag={expandOnDrag}
        ability={ability}
      />
    )
  }

  // KPI表示の時、合計・小計の考慮
  //
  // KPIピボット表示時、列に並べ替えが指定されているとツリーの形式が変わる（子レコードが存在しなくなる）
  // そのため、ルートノードに子レコードが存在するかどうかによって、小計・合計セルの判定を分けている
  const hasNoChildrenRecord = rootRecordNode.children.length === 0
  const totalStringNodes = getTotalStringNodes({ recordNode, hasNoChildrenRecord, column })
  const isSubTotalOrTotal = totalStringNodes.some(Boolean)
  const isTotal = isSubTotalOrTotal && totalStringNodes.length === 1
  const isSubTotal = isSubTotalOrTotal && totalStringNodes.length > 1
  const isSubTotalChild = totalStringNodes.filter(Boolean).length > 1
  const isTotalChild = totalStringNodes.filter(Boolean).length > 1 && totalStringNodes.at(-1) === true

  // kpiで、行軸の小計の表示は、もはやここで対応してしまう
  if (column.type !== 'kpi') {
    const totalStyleCommon = {
      height: defaultStyle.height,
      borderBottom: `1px ${RSheetsStyle.color.border} solid`,
      borderTop: `1px ${RSheetsStyle.color.border} solid`,
      fontWeight: 'bold',
    }
    if (isTotal) {
      return (
        <TotalCellWrapper
          type="total"
          style={{
            ...totalStyleCommon,
            backgroundColor: RSheetsStyle.color.kpiTotalCell,
          }}
        >
          {t(`合計`)}
        </TotalCellWrapper>
      )
    }

    if (isSubTotal) {
      // 現在の列に対応するdimenstion。KPIプレビュー、ダッシュボードで使用
      // column.indexは行番号表示用に追加された一列を加味した値なので、-1する
      const dimension = context.pivot.rows[column.index - 1]
      const dimensionName = `dimension${column.index}`
      const isKpi = dimension?.key === CORE_CONSTANT.KPI_PIVOT_KPI_ID_COLUMN_NAME
      const isKpiGroup = dimension?.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_NAME_COLUMN_NAME

      if (isKpi) {
        return <></>
      }

      if (isKpiGroup) {
        const kpiGroupName = hasNoChildrenRecord ? recordNode.attributes[dimensionName] : recordNode.id
        if (typeof kpiGroupName !== 'string') {
          return <></>
        }

        return (
          <TotalCellWrapper
            type="subtotal"
            style={{
              ...totalStyleCommon,
              backgroundColor: RSheetsStyle.color.kpiSubTotalCell,
              fontWeight: 'normal',
            }}
          >
            <SubTotalWithLabel label={kpiGroupName} />
          </TotalCellWrapper>
        )
      }

      return (
        <TotalCellWrapper
          type="subtotal"
          style={{
            ...totalStyleCommon,
            backgroundColor: RSheetsStyle.color.kpiSubTotalCell,
          }}
        >
          {t(`小計`)}
        </TotalCellWrapper>
      )
    }

    if (isTotalChild) {
      return (
        <TotalCellWrapper
          type="total-child"
          style={{
            ...totalStyleCommon,
            backgroundColor: RSheetsStyle.color.kpiTotalCell,
          }}
        />
      )
    }

    if (isSubTotalChild) {
      return (
        <TotalCellWrapper
          type="subtotal-child"
          style={{
            ...totalStyleCommon,
            backgroundColor: RSheetsStyle.color.kpiSubTotalCell,
          }}
        />
      )
    }
  }

  return (
    <RecordCell
      recordNode={recordNode}
      column={column}
      context={context}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      innerRowIndex={innerRowIndex}
      rootRecordNode={rootRecordNode}
      columns={columns}
      setMainCursorByRecordId={setMainCursorByRecordId}
      view={view}
      isFieldChanged={isFieldChanged}
      isNewRecord={isNewRecord}
      overrideValue={overrideValue}
      height={height}
      value={value}
      defaultStyle={defaultStyle}
      isSubTotal={isSubTotal}
      isTotal={isTotal}
      isTotalChild={isTotalChild}
      snapshot={snapshot}
      setDragging={setDragging}
      expandOnDrag={expandOnDrag}
      progress={progress}
    />
  )
}

const RecordCell = ({
  recordNode,
  column,
  context,
  rowIndex,
  columnIndex,
  innerRowIndex,
  rootRecordNode,
  columns,
  setMainCursorByRecordId,
  view,
  isFieldChanged,
  isNewRecord,
  overrideValue,
  height,
  value,
  defaultStyle,
  isSubTotal,
  isTotal,
  isTotalChild,
  setDragging,
  expandOnDrag,
  progress,
}: {
  recordNode: RSheetRecordNode
  column: PivotRowColumn<ViewQueryRecordNode>
  context: KpiTableContext
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  rootRecordNode: RSheetRecordNode
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  view: ViewFieldsFragment
  isFieldChanged: boolean
  isNewRecord: boolean
  overrideValue?: unknown
  height: number
  value: unknown
  defaultStyle: CSSProperties
  isSubTotal: boolean
  isTotal: boolean
  isTotalChild: boolean
  snapshot: Record<string, Record<string, unknown>>
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  progress: ProgressType
}): ReactElement => {
  const { organization } = useMe()

  const changed = isFieldChanged || isNewRecord
  const currentValue = changed ? overrideValue : value
  const kpiRelatedStyle = getStyleByKpi({ column, isSubTotal, isTotal, isTotalChild })

  const mobileContextMenuEnabled = organizationHasFeature(organization, 'enable_mobile_context_menu')
  const longPressEventHandler = useLongPress(
    (event) => {
      if (!('touches' in event) || event.touches[0] === undefined) {
        return
      }
      const contextMenuPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
      openContextMenu({
        rootRecordNode,
        columnIndex,
        innerRowIndex,
        contextMenuPosition,
        disabled: false,
        setMainCursorByRecordId,
      })
    },
    {
      isPreventDefault: false,
      delay: 300,
    },
  )

  const boxStyle: CSSProperties = {
    ...defaultStyle,
    padding: '10px 10px',
    ...kpiRelatedStyle,
    fontSize: 14,
    ...(height === 1
      ? {
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }
      : {}),
  }

  return (
    <div
      key={column.key}
      data-e2e={`rsheet-cell-${rowIndex}-${columnIndex}-${innerRowIndex}`}
      style={boxStyle}
      {...(mobileContextMenuEnabled ? longPressEventHandler : {})}
      onContextMenu={(event) => {
        const contextMenuPosition = {
          x: event.clientX,
          y: event.clientY,
        }
        openContextMenu({
          rootRecordNode,
          columnIndex,
          innerRowIndex,
          contextMenuPosition,
          disabled: false,
          setMainCursorByRecordId,
        })
        event.preventDefault()
        event.stopPropagation()
      }}
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey && rootRecordNode.id !== undefined) {
          setMainCursorByRecordId({
            position: {
              recordId: rootRecordNode.id,
              columnIndex,
              innerRowIndex,
            },
            option: {
              expand: true,
            },
          })
          return
        }
        if (column.type === 'rowKpi') {
          const contextMenuPosition = {
            x: event.clientX,
            y: event.clientY,
          }
          openContextMenu({
            rootRecordNode,
            columnIndex,
            innerRowIndex,
            contextMenuPosition,
            disabled: false,
            setMainCursorByRecordId,
          })
          return
        }
        toggleEditMode({ rootRecordNode, columnIndex, innerRowIndex, disabled: false, setMainCursorByRecordId })
      }}
    >
      <KpiRowCellBody
        column={column}
        value={currentValue}
        recordNode={recordNode}
        height={height}
        style={{
          color: boxStyle.color,
          backgroundColor: boxStyle.backgroundColor,
        }}
        context={context}
        setCursorAndUseEditMode={() => {
          setEditMode({ rootRecordNode, columnIndex, innerRowIndex, disabled: false, setMainCursorByRecordId })
        }}
        columns={columns}
        view={view}
        progress={progress}
      />
    </div>
  )
}

const TotalCellWrapper = ({
  type,
  style,
  children,
}: {
  type: 'total' | 'subtotal' | 'total-child' | 'subtotal-child'
  style: CSSProperties
  children?: React.ReactNode
}): ReactElement => (
  <div className={`${type} flex items-center justify-center align-middle`} style={style}>
    {children}
  </div>
)

const SubTotalWithLabel = ({ label }: { label: string }): ReactElement => (
  <div className="flex w-full flex-col items-center justify-center gap-2">
    <span className="block w-full text-center" style={{ fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-all' }}>
      {label}
    </span>
    <span className="block font-bold">{t(`小計`)}</span>
  </div>
)

const getTotalStringNodes = ({
  recordNode,
  column,
  hasNoChildrenRecord,
}: {
  recordNode: ViewQueryRecordNodeWithParent
  column: PivotRowColumn<ViewQueryRecordNode>
  hasNoChildrenRecord: boolean
}): boolean[] => {
  if (hasNoChildrenRecord) {
    const dimensionKeys = Array.range(1, column.index).map((index) => `dimension${index}`)
    return dimensionKeys.map((key) => recordNode.attributes[key] === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING)
  }

  const parents = getRecordNodeParents(recordNode)
  return parents.map((x) => Record.values(x.attributes).includes(CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING))
}

const getStyleByKpi = ({
  isTotal,
  isSubTotal,
  isTotalChild,
  column,
}: {
  isTotal: boolean
  isSubTotal: boolean
  isTotalChild: boolean
  column: PivotRowColumn<ViewQueryRecordNode>
}): CSSProperties => {
  // eslint-disable-next-line sonarjs/todo-tag
  const withoutPadding = column.type === 'rowKpi' || column.type === 'kpi' // TODO

  const getBackgroundColor = (): string | undefined => {
    if (column.type === 'kpi') {
      if (isTotal || isTotalChild) {
        return RSheetsStyle.color.kpiTotalCell
      }
      if (isSubTotal) {
        return RSheetsStyle.color.kpiSubTotalCell
      }

      return RSheetsStyle.color.kpiCell
    }
    // typeがkpiでない = 行軸の列ではなく、このパターンは別で処理しているはず
  }
  return {
    backgroundColor: getBackgroundColor(),
    padding: withoutPadding ? 0 : '10px 10px',
  }
}

const KpiRowEmptyCell = ({
  rowIndex,
  columnIndex,
  innerRowIndex,
  defaultStyle,
  setDragging,
  expandOnDrag,
  ability,
}: {
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  defaultStyle: CSSProperties
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { expand, setContextMenuPosition } = useCursorMutation(ability)

  return (
    <div
      style={{
        ...defaultStyle,
        fontSize: 14,
        padding: '10px 10px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        transition: RSheetsStyle.cellTransition,
        textAlign: 'center',
        color: RSheetsStyle.color.gray,
      }}
      className="bg-gray-50"
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey) {
          expand({
            rowIndex,
            columnIndex,
            innerRowIndex,
          })
        }
      }}
      onContextMenu={(event) => {
        setContextMenuPosition(
          {
            columnIndex,
            rowIndex,
            innerRowIndex,
          },
          {
            x: event.clientX,
            y: event.clientY,
          },
        )
        event.preventDefault()
        event.stopPropagation()
      }}
    />
  )
}

const KpiRowCellBody = ({
  column,
  context,
  height,
  recordNode,
  setCursorAndUseEditMode,
  style,
  value,
  columns,
  view,
  progress,
}: {
  column: PivotRowColumn<ViewQueryRecordNode>
  value: unknown
  recordNode: ViewQueryRecordNodeWithParent
  height: number
  style: {
    color?: string // 通常RSheetsCell側で定義されるが、SelectOptionCellなど一部でコンポーネント側で上書きが必要なため
    backgroundColor?: string // 同上
  }
  setCursorAndUseEditMode: () => void
  context: KpiTableContext
  columns: Array<
    PivotSystemColumn<ViewQueryRecordNode> | PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  >
  view: ViewFieldsFragment
  progress: ProgressType
}): ReactElement => {
  if (column.width === MINIMUM_WIDTH) {
    return <></>
  }
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 手抜き
  if (value === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
    return <StringCell value={`小計`} height={height} column={column} recordNode={recordNode} />
  }
  if (value === CORE_CONSTANT.KPI_PIVOT_NULL_STRING) {
    return <StringCell value={'-'} height={height} column={column} recordNode={recordNode} />
  }

  switch (column.metaType) {
    case 'record_url': {
      return <RecordUrlCell />
    }
    case 'relation': {
      return <ReferenceCell value={value} column={column} recordNode={recordNode} style={style} />
    }
    case 'id': {
      if (column.type === 'user') {
        return <UserCell value={value} />
      }
      return <ReferenceCell value={value} column={column} recordNode={recordNode} style={style} />
    }
    case 'url': {
      return <UrlCell value={value} column={column} style={style} setCursorAndUseEditMode={setCursorAndUseEditMode} />
    }
    case 'phone_number': {
      return <UrlCell value={value} column={column} style={style} setCursorAndUseEditMode={setCursorAndUseEditMode} />
    }
    case 'email': {
      return <UrlCell value={value} column={column} style={style} setCursorAndUseEditMode={setCursorAndUseEditMode} />
    }
    case 'html': {
      return <HtmlCell value={value} />
    }
    default: {
      break
    }
  }

  if ((column.selectOptions ?? []).length > 0) {
    if (column.metaType === 'multi_select') {
      return <MultiSelectOptionCell value={value} column={column} />
    }
    return <SelectOptionCell value={value} column={column} />
  }

  const { type } = column
  switch (type) {
    case 'kpi' as const: {
      return (
        <KpiCell
          recordNode={recordNode}
          context={context}
          column={column}
          columns={columns}
          view={view}
          progress={progress}
        />
      )
    }
    case 'rowKpi' as const: {
      return <RowKpiCell value={value} column={column} recordNode={recordNode} context={context} columns={columns} />
    }
    case 'user' as const: {
      return <UserCell value={value} />
    }
    case 'date' as const: {
      return <DateCell value={value} />
    }
    // cell側で出しわけしたので、ここには来ない
    case 'string' as const: {
      return <StringCell value={value} column={column} recordNode={recordNode} height={height} />
    }
    case 'reference' as const: {
      return <ReferenceCell value={value} column={column} recordNode={recordNode} style={style} />
    }
  }
}

const UserCell = ({ value }: { value: unknown }): ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const id = value as string
  const getUser = useAppSelector(selectGetUser)
  const user = getUser(id)
  return (
    <div className="pt-2 text-center" style={{ marginTop: -6 }}>
      {user === undefined ? (
        id
      ) : (
        <>
          <UserAvatar name={user.name} imageUrl={user.image_url ?? ''} size="large" />
          <br />
          {user.name}
        </>
      )}
    </div>
  )
}

const RecordUrlCell = (): ReactElement => (
  // リンクを開くのはsetMainCursorByRecordIdMutation側で行うため、ここではaタグは不要
  // <a href={value as string} target="_blank" style={{ color: '#aaa' }}>
  <div style={{ color: '#aaa' }} className="w-full cursor-pointer">
    <LinkOutlined />
  </div>
  // </a>
)

const getUrlProtocol = (column: PivotRowColumn<ViewQueryRecordNode>): string => {
  const oid = getOrganizationIdFromPath()
  const callToOrganizationIds: string[] = [`275afebc-0fef-4f0c-9101-e36aa7816678`]
  switch (column.metaType) {
    case 'phone_number': {
      return callToOrganizationIds.includes(oid) ? 'callto:' : 'tel:'
    }
    case 'email': {
      return 'mailto:'
    }
    default: {
      return ''
    } // http
  }
}

// url, tel, emailなどのリンク付きセル
const UrlCell = ({
  value,
  column,
  style: { color },
  setCursorAndUseEditMode,
}: {
  value: unknown
  column: PivotRowColumn<ViewQueryRecordNode>
  style: {
    color?: string
  }
  setCursorAndUseEditMode: () => void
}): ReactElement => {
  const element = useHover((hovered) => {
    const strings = z.string().array().safeParse(value)
    const string = z.string().safeParse(value)
    // eslint-disable-next-line sonarjs/no-nested-conditional
    const emails = strings.success ? strings.data : string.success ? [string.data] : undefined

    if (emails === undefined || emails.length === 0) {
      return <></>
    }

    return (
      <div style={{ width: '100%', height: '100%', padding: '0 2px' }}>
        {emails.map((email, index) => (
          <a
            key={`${email}_${index}`}
            href={`${getUrlProtocol(column)}${email}`}
            target={
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              ['url', 'email'].includes(column.metaType as string) ? '_blank' : ''
            }
            style={{ textDecoration: 'underline', color }}
            onPointerDown={(event) => {
              // RsheetsCellでstate更新をonPointerDownで行っており、それをe.stopPropagation()で止める必要がある。
              // onClickはクリックを離した時に発火するのに対して、onPointerDownはクリックを押した時に発火するため、
              // onClickだと間に合わないのでonPointerDownでもe.stopPropagation()をする。
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()

              // phoneのとき、miitelの判定を行う
              if (column.metaType === 'phone_number') {
                const miitel = getMiitelWidget()
                if (miitel !== undefined) {
                  miitel.call(email)
                  event.preventDefault()
                }
              }
            }}
            rel="noreferrer"
          >
            {email}
          </a>
        ))}
        <Button
          icon={<EditOutlined />}
          style={{
            position: 'absolute',
            top: 4,
            right: 2,
            opacity: hovered ? 100 : 0,
          }}
          onClick={(event) => {
            event.stopPropagation()
            setCursorAndUseEditMode()
          }}
        />
      </div>
    )
  })

  return <>{element}</>
}

const ReferenceCell = ({
  value,
  column,
  recordNode,
  style: { backgroundColor, color },
}: {
  value: unknown
  column: PivotRowColumn<ViewQueryRecordNode>
  recordNode: ViewQueryRecordNodeWithParent
  style: {
    color?: string
    backgroundColor?: string
  }
}): ReactElement => {
  const label = column.label(recordNode)
  const colorStyle = {
    color,
    borderColor: color,
    backgroundColor,
  }

  if (
    value === undefined ||
    value === null ||
    !(typeof value === 'string' ? value !== '' : !Array.isArray(value) || value.length > 0)
  ) {
    return <span style={{ ...colorStyle, color: colorStyle.color ?? '#ccc' }}></span>
  }
  if (label === undefined || label === null) {
    return (
      <span style={{ ...colorStyle, color: colorStyle.color ?? '#ccc' }}>
        <>
          {t('該当なし')} id: {value}
        </>
      </span>
    )
  }
  // タグで表示するとpaddingの分でむしろ見づらいため
  return (
    <div>
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        label as string
      }
    </div>
  )
}

const SelectOptionTag = ({
  value,
  selectOptions,
}: {
  value: unknown
  selectOptions: RSheetsSelectOption[]
}): ReactElement => {
  if (value === undefined || value === '' || value === null || !['string', 'number'].includes(typeof value)) {
    return <></>
  }

  const option = selectOptions.find((option) => option.value === value)

  // タグで表示するとpaddingの分でむしろ見づらいため
  return (
    <div>
      {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        option?.label ?? (value as string)
      }
    </div>
  )
}

const SelectOptionCell = ({
  value,
  column: { selectOptions },
}: {
  value: unknown
  column: PivotRowColumn<ViewQueryRecordNode>
}): ReactElement => <SelectOptionTag value={value} selectOptions={selectOptions ?? []} />

const MultiSelectOptionCell = ({
  value,
  column,
}: {
  value: unknown
  column: PivotRowColumn<ViewQueryRecordNode>
}): ReactElement => {
  const values = Array.isArray(value) ? value : [value]
  const validated = z.union([z.string(), z.number()]).array().safeParse(values)

  const selectedOptions = validated.success
    ? validated.data
        // 2023/05 ELT側の不具合で、Salesforceの複数選択項目が空のとき、['']という1要素の配列になってしまう
        // ELT側で対応すべき処理だが、即時の対応が難しかったため、一旦こちらで対応
        // 同様の処理をMultiSelectOptionInputCellでもやっている
        // https://github.com/salescore-inc/issues/issues/198
        .filter((v) => (typeof v === 'string' ? v !== '' : true))
        .map(
          (v) =>
            column.selectOptions?.find((option) => option.value === v) ?? {
              // eslint-disable-next-line sonarjs/no-nested-conditional
              label: (typeof v === 'string' ? v !== '' : true) ? v : `(空文字列)`, // 上記でフィルタしているので不要なはずだが一応
              color: 'gray',
            },
        )
    : []

  if (value === undefined || value === '') {
    return <></>
  }

  if (!validated.success) {
    return <></>
  }

  return (
    <Select
      mode="multiple"
      style={{ width: '100%', marginTop: -6 }}
      maxTagCount="responsive"
      value={selectedOptions.map((option) => `${option.label}`)}
      open={false}
      removeIcon={null}
      suffixIcon={null}
      variant="borderless"
      tagRender={({ value: tagValue, isMaxTag, label }) => {
        if (isMaxTag && typeof label === 'string') {
          return (
            <Tag
              style={{
                fontSize: 14,
                padding: '3px 10px',
                marginInlineEnd: 0,
              }}
            >
              {label.slice(0, -3)}
            </Tag>
          )
        }
        const option = selectedOptions.find((o) => `${o.label}` === tagValue)

        return (
          <Tag
            color={option?.color ?? 'gray'}
            style={{
              fontSize: 14,
              padding: '3px 10px',
              marginInlineEnd: 2,
            }}
          >
            {option?.label ?? tagValue}
          </Tag>
        )
      }}
    />
  )
}

const DateCell = ({ value }: { value: unknown }): ReactElement => (
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  <pre style={{ overflow: 'hidden' }}>{value as string}</pre>
)

const StringCell = ({
  value,
  height,
  column,
  recordNode,
}: {
  value: unknown
  height: number
  column: PivotRowColumn<ViewQueryRecordNode>
  recordNode: ViewQueryRecordNodeWithParent
}): ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const label = column.label(recordNode) as string // 2023/08 StringCellだがラベル値があるのは、現状はsalescore_users_with_groupのみ
  const textLineSize = Math.floor(height * 1.82)
  const lineClampStyle: CSSProperties = {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: textLineSize,
    display: '-webkit-box',
  }
  const stringValue =
    typeof value === 'string'
      ? value
      : // eslint-disable-next-line sonarjs/no-nested-conditional
        typeof value === 'number'
        ? `${value}`
        : // eslint-disable-next-line sonarjs/no-nested-conditional
          value === undefined || value === null
          ? ``
          : JSON.stringify(value)
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- label自体がcastされている
  const text = label ?? stringValue
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: ここでpopoverしてしまうとpadding分を考慮できずhoverの判定が微妙になる。構造を変える必要がある。
  return (
    <Popover content={<pre style={{ width: 400 }}>{text}</pre>} placement="bottomLeft">
      {
        // 変則的だが、1行のときはspanで親要素divのtextOverflowによって三点リーダを表示し、1行より大きい場合はwebkit-line-clampを使って表示
        height === 1 ? <span>{text}</span> : <p style={lineClampStyle}>{text}</p>
      }
    </Popover>
  )
}

const HtmlCell = ({ value }: { value: unknown }): ReactElement => {
  if (typeof value !== 'string') {
    return <></>
  }
  const htmlContent = <SanitizedHtml rawHtmlText={value} />

  return (
    <Popover
      content={
        // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- html-cell-popover は main.less にスタイル定義あり
        <div className="html-cell-popover" style={{ width: 600, maxHeight: '60vh', overflow: 'scroll' }}>
          {htmlContent}
        </div>
      }
      placement="rightTop"
    >
      <div>{htmlContent}</div>
    </Popover>
  )
}
